import {v4} from 'uuid';
import {useSelector} from 'react-redux';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import React, {useCallback, useMemo, useState} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';

import {ACCEPTED_FILES} from '../const';
import {RootState} from '../../../store';
import {localTypeToBinary} from '../helpers';
import {ListViewer} from '../DataReview/ListViewer';
import {DropArea} from '../../../base-components/StudioDropArea';
import Waiting from '../../../base-components/StudioWaiting/Waiting';
import {BINARY_TYPES} from '../../../types/dataService/DatasetMetadata';
import {StudioContentLayout} from '../../../base-components/StudioContentLayout';
import {getFileTypeFromFilename} from '../../../base-components/StudioDataset/const';
import {FileViewer, ItemType} from '../../../base-components/StudioDataset/FileViewer';

import './UploadBinariesDialog.scss';

type SortConfig = {
  key: keyof ItemType;
  direction: 'asc' | 'desc';
};

type DataUploadProps = {
  open: boolean;
  uploadFiles: ItemType[];

  onClose?: () => void;
  onSubmit?: () => void;
  onChangeUploadingFilesList: (files: ItemType[]) => void;
};

export const UploadBinariesDialog = ({
  open = false,
  uploadFiles,

  onChangeUploadingFilesList,
  onClose = () => {},
  onSubmit = () => {},
}: DataUploadProps) => {
  const intl = useIntl();
  const datasetId = useSelector((state: RootState) => state.dataset.data?.id);

  const [isLoading, setIsLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: 'filename',
    direction: 'asc',
  });
  // TODO: activate pagination and selection
  // const [selections, setSelections] = useState<string[]>([]);
  // const [page, setPage] = useState<number>(0);

  const fileToImage = useCallback((file: File) => {
    return new Promise<HTMLImageElement>((resolve, reject) => {
      const fileReader = new FileReader();

      fileReader.onload = () => {
        const image = new Image();

        image.onload = () => {
          resolve(image);
        };

        image.onerror = () => {
          reject();
        };

        if (typeof fileReader.result === 'string') {
          image.src = fileReader.result;
        } else {
          reject();
        }
      };

      fileReader.onerror = () => {
        reject();
      };

      fileReader.readAsDataURL(file);
    });
  }, []);

  const onSubmitLocal = useCallback(async () => {
    if (datasetId && uploadFiles.length) {
      // start uploading
      onSubmit();
      onClose();
    }
  }, [datasetId, uploadFiles, onSubmit, onClose]);

  const handleUploadedFiles = useCallback(
    async (files: File[]) => {
      setIsLoading(true);

      const newUploadedFiles: ItemType[] = await Promise.all(
        files.map(async file => {
          let image: HTMLImageElement | undefined;

          try {
            image = await fileToImage(file);
          } catch (e) {}

          const type = getFileTypeFromFilename(file.name);

          return {
            file,
            type,
            id: v4(),
            selected: false,
            url: image?.src,
            size: file.size,
            width: image?.width,
            filename: file.name,
            height: image?.height,
            lastEdited: new Date(file.lastModified),
            binaryType: localTypeToBinary(type) as BINARY_TYPES,
          };
        })
      );

      setIsLoading(false);
      onChangeUploadingFilesList([...uploadFiles, ...newUploadedFiles]);
    },
    [uploadFiles, fileToImage, onChangeUploadingFilesList]
  );

  const onSort = useCallback((colId, dir) => {
    setSortConfig({key: colId, direction: dir});
  }, []);

  const sortedFiles = useMemo(() => {
    let finalFiles = uploadFiles;

    if (sortConfig) {
      finalFiles = [...uploadFiles].sort((fileA: ItemType, fileB: ItemType) => {
        const valA = fileA[sortConfig.key];
        const valB = fileB[sortConfig.key];

        const multiplier = sortConfig.direction === 'asc' ? 1 : -1;

        return multiplier * (valA < valB ? -1 : valA > valB ? 1 : 0);
      });
    }

    return finalFiles;
  }, [uploadFiles, sortConfig]);

  return (
    <Dialog
      className="confirm-dialog upload-binaries-dialog"
      open={open}
      onClose={onClose}
      aria-labelledby="upload-binaries-dialog"
      aria-describedby="upload-binaries-dialog-description"
      data-testid="upload-binaries-dialog"
      fullWidth
      maxWidth="lg"
    >
      <DialogTitle>
        <div className="confirm-dialog__title-container">
          <FormattedMessage id="datasetCreation.uploadBinaries.title" />
        </div>
      </DialogTitle>
      <DialogContent>
        {isLoading ? (
          <div className="upload-binaries-dialog__loading">
            <Waiting
              diameter={100}
              loadingText={intl.formatMessage({
                id: 'datasetCreation.uploadBinaries.loading',
              })}
            />
          </div>
        ) : (
          <div className="data-upload">
            <DropArea
              accept={ACCEPTED_FILES}
              className="data-upload__drop"
              onChange={handleUploadedFiles}
              size={uploadFiles.length ? 'xs' : 'xlg'}
            />
          </div>
        )}

        {!!sortedFiles.length && (
          <div className="upload-binaries-dialog__list">
            <StudioContentLayout>
              {() => (
                <FileViewer
                  page={0}
                  onSort={onSort}
                  itemList={sortedFiles}
                  itemGroup={[
                    {
                      list: sortedFiles,
                      // TODO: change this later when dealing with View responsiveness
                      totalElements: sortedFiles.length / 50,
                    },
                  ]}
                  view="list"
                  listViewRender={({...args}) => (
                    <ListViewer
                      enableSelection={false}
                      onDelete={itemId => {
                        onChangeUploadingFilesList(
                          sortedFiles.filter(item => item.id !== itemId)
                        );
                      }}
                      {...args}
                      columns={[...args.columns, {id: 'delete', label: '', width: 60}]}
                    />
                  )}
                  gridViewRender={null}
                  labelViewRender={null}
                />
              )}
            </StudioContentLayout>
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          <FormattedMessage id="form.cancel" />
        </Button>
        <Button
          data-testid="uploading-binaries-confirm-dialog-keep"
          color="primary"
          disableElevation
          variant="contained"
          disabled={!uploadFiles.length}
          onClick={onSubmitLocal}
        >
          <FormattedMessage id="form.submit" />
        </Button>
      </DialogActions>
    </Dialog>
  );
};
